import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FaChevronRight } from '@react-icons/all-files/fa/FaChevronRight'
import { FaMapMarkerAlt } from '@react-icons/all-files/fa/FaMapMarkerAlt'
import { apiCallBegan } from '../../store/api'
import styles from './LocationAutocomplete.module.scss'

const newSessionToken = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`.slice(0, 36)
}

const LocationAutocomplete = ({
  allowManualFallback = true,
  className = '',
  defaultValue = '',
  disabled = false,
  fullWidth = false,
  manualFallback = false,
  market = 'miami',
  onClear,
  onLoadingChange,
  onManualFallback,
  onSelect,
  onUnavailable,
  selectedPlace = null,
  variant = 'v2',
}) => {
  const dispatch = useDispatch()
  const inputId = useId()
  const listId = `${inputId}-suggestions`
  const requestNumber = useRef(0)
  const resultsRef = useRef(null)
  const sessionToken = useRef(newSessionToken())
  const selectedDescription = useRef(defaultValue)
  const onLoadingChangeRef = useRef(onLoadingChange)
  const onUnavailableRef = useRef(onUnavailable)
  const [query, setQuery] = useState(defaultValue)
  const [suggestions, setSuggestions] = useState([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [manualAvailable, setManualAvailable] = useState(false)
  const [selection, setSelection] = useState(selectedPlace)
  const manualFallbackAllowed = allowManualFallback && Boolean(onManualFallback)
  const rootClassName = `${styles.root} ${variant === 'legacy' ? styles.legacy : styles.v2} ${fullWidth ? styles.fullWidth : ''} ${className}`.trim()

  useEffect(() => {
    onUnavailableRef.current = onUnavailable
  }, [onUnavailable])

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange
  }, [onLoadingChange])

  const updateLoading = useCallback(nextLoading => {
    setLoading(nextLoading)
    onLoadingChangeRef.current?.(nextLoading)
  }, [])

  useEffect(() => () => onLoadingChangeRef.current?.(false), [])

  useEffect(() => {
    if (!selectedPlace?._id && !selectedPlace?.provider?.place_id) return
    setSelection(selectedPlace)
    selectedDescription.current = selectedPlace.formattedAddress || selectedPlace.provider.formatted_address || ''
    setQuery(selectedDescription.current)
  }, [selectedPlace])

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 3 || trimmed === selectedDescription.current) {
      setSuggestions([])
      updateLoading(false)
      return undefined
    }

    const currentRequest = ++requestNumber.current
    updateLoading(true)
    setMessage('')
    const timer = setTimeout(async () => {
      const response = await dispatch(apiCallBegan({
        url: '/location/autocomplete',
        method: 'POST',
        data: { input: trimmed, market, sessionToken: sessionToken.current },
        returnErrorData: true,
      }))
      if (currentRequest !== requestNumber.current) return
      updateLoading(false)
      if (response?.errorMessage) {
        setSuggestions([])
        setMessage(manualFallbackAllowed
          ? response.errorMessage
          : 'Address search is temporarily unavailable. Please try again or use a different search.')
        setManualAvailable(true)
        onUnavailableRef.current?.(response.errorMessage)
        return
      }
      const nextSuggestions = response?.suggestions || []
      setSuggestions(nextSuggestions)
      setManualAvailable(!nextSuggestions.length)
      if (!nextSuggestions.length) setMessage(manualFallbackAllowed
        ? 'We could not find a matching address. Try another search or enter it manually.'
        : 'We could not find a matching address. Try another venue name or street address.')
      setActiveIndex(-1)
    }, 280)

    return () => clearTimeout(timer)
  }, [dispatch, manualFallbackAllowed, market, query, updateLoading])

  useEffect(() => {
    if (!suggestions.length || typeof window === 'undefined') return undefined
    const frame = window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      resultsRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
      })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [suggestions.length])

  const chooseSuggestion = async suggestion => {
    const currentRequest = ++requestNumber.current
    updateLoading(true)
    setMessage('')
    setSuggestions([])
    const response = await dispatch(apiCallBegan({
      url: '/location/place-details',
      method: 'POST',
      data: { placeId: suggestion.placeId, name: suggestion.mainText, sessionToken: sessionToken.current },
      returnErrorData: true,
    }))
    if (currentRequest !== requestNumber.current) return
    if (response?.errorMessage) {
      updateLoading(false)
      setMessage(manualFallbackAllowed
        ? response.errorMessage
        : 'We could not verify that address. Please choose a different Google result.')
      setManualAvailable(true)
      onUnavailableRef.current?.(response.errorMessage)
      return
    }

    const description = response.formattedAddress || suggestion.description
    onSelect?.(response)
    selectedDescription.current = description
    setQuery(description)
    setSelection(response)
    setManualAvailable(false)
    setMessage('')
    sessionToken.current = newSessionToken()
    updateLoading(false)
  }

  const changeAddress = () => {
    requestNumber.current += 1
    selectedDescription.current = ''
    setSelection(null)
    setQuery('')
    setSuggestions([])
    setMessage('')
    setManualAvailable(false)
    updateLoading(false)
    sessionToken.current = newSessionToken()
    onClear?.()
  }

  const handleKeyDown = event => {
    if (!suggestions.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(index => (index + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => index <= 0 ? suggestions.length - 1 : index - 1)
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault()
      chooseSuggestion(suggestions[activeIndex])
    } else if (event.key === 'Escape') {
      setSuggestions([])
      setActiveIndex(-1)
    }
  }

  if (selection) return (
    <div className={rootClassName}>
      <span className={styles.label}>Selected address</span>
      <div className={styles.selectedAddress}>
        <span className={styles.selectedIcon} aria-hidden='true'><FaMapMarkerAlt /></span>
        <span className={styles.selectedCopy}>
          {selection.isVenue && selection.name && <strong>{selection.name}</strong>}
          <span>{selection.formattedAddress || selection.provider?.formatted_address}</span>
        </span>
        <button className={styles.changeButton} type='button' onClick={changeAddress} disabled={disabled}>Change address</button>
      </div>
      <span className={styles.selectedHint}>This verified address will be used to identify the event location.</span>
    </div>
  )

  return (
    <div className={rootClassName}>
      <label className={styles.label} htmlFor={inputId}>Search for a venue or address</label>
      <span className={styles.hint}>Start typing, then select the correct location to complete the address.</span>
      <div className={styles.control}>
        <input
          id={inputId}
          className={styles.input}
          type='text'
          autoComplete='off'
          role='combobox'
          aria-autocomplete='list'
          aria-expanded={suggestions.length > 0}
          aria-controls={listId}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          disabled={disabled}
          placeholder='Venue name or street address'
          value={query}
          onChange={event => {
            requestNumber.current += 1
            selectedDescription.current = ''
            setQuery(event.target.value)
            setMessage('')
            updateLoading(false)
          }}
          onKeyDown={handleKeyDown}
        />
        {loading && <span className={styles.loading} role='status'>Searching…</span>}
        {suggestions.length > 0 && (
          <div className={styles.results} ref={resultsRef}>
            <div className={styles.resultsHeader}>
              <strong>Suggested locations</strong>
              <span>Select one to complete the address</span>
            </div>
            <div className={styles.resultsList} id={listId} role='listbox' aria-label='Address suggestions'>
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.placeId}
                  id={`${listId}-${index}`}
                  type='button'
                  role='option'
                  aria-selected={activeIndex === index}
                  className={`${styles.option} ${activeIndex === index ? styles.activeOption : ''}`}
                  onMouseDown={event => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => chooseSuggestion(suggestion)}
                >
                  <span className={styles.optionIcon} aria-hidden='true'><FaMapMarkerAlt /></span>
                  <span className={styles.optionCopy}>
                    <strong className={styles.optionTitle}>{suggestion.mainText}</strong>
                    {suggestion.secondaryText && <span className={styles.optionAddress}>{suggestion.secondaryText}</span>}
                  </span>
                  <FaChevronRight className={styles.optionArrow} aria-hidden='true' />
                </button>
              ))}
            </div>
            <div className={styles.attribution}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src='https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png' alt='Powered by Google' />
            </div>
          </div>
        )}
      </div>
      {message && <span className={styles.message} role='status'>{message}</span>}
      {manualAvailable && manualFallbackAllowed && !manualFallback && (
        <button className={styles.manualButton} type='button' onClick={onManualFallback}>Enter address manually</button>
      )}
    </div>
  )
}

export default LocationAutocomplete
